import { useState } from "react";
import client, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Btn, Field, Input } from "@/components/kit";
import { CHANNELS } from "@/lib/constants";
import { Loader2, Wand2 } from "lucide-react";

const toDateInput = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function IdeaDropModal({ idea, date, onClose, onDone }) {
  const [channels, setChannels] = useState(["Instagram", "Facebook"]);
  const [dateStr, setDateStr] = useState(toDateInput(date));
  const [time, setTime] = useState("09:00");
  const [busy, setBusy] = useState(false);
  const toggle = (c) => setChannels(channels.includes(c) ? channels.filter((x) => x !== c) : [...channels, c]);

  const submit = async () => {
    if (!channels.length) return toast.error("Selecione ao menos uma rede");
    const [y, mo, da] = dateStr.split("-").map(Number);
    const [hh, mm] = time.split(":").map(Number);
    const dt = new Date(y, mo - 1, da, hh || 0, mm || 0, 0, 0);
    if (dt < new Date()) return toast.error("Escolha uma data e horário no futuro");
    setBusy(true);
    try {
      const { data } = await client.post("/campaigns/omni", {
        brand_id: idea.brand_id, theme: idea.text, channels, scheduled_at: dt.toISOString(), pillar: idea.pillar || null,
      });
      toast.success(`${data.count} post(s) gerados para ${dt.toLocaleDateString("pt-BR")} às ${time}. Aprove na Central de Aprovação.`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="idea-drop-modal" onClick={() => !busy && onClose()}>
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-lg font-extrabold text-slate-900">Criar posts com esta ideia</h3>
        <p className="mt-1 text-sm text-slate-600" data-testid="idea-drop-theme">“{idea.text}”</p>
        <p className="mt-1 text-xs text-slate-400">A IA gera um post por rede, já com data. Ao aprovar na Central de Aprovação, ele fica agendado automaticamente.</p>
        <div className="mt-4"><Field label="Redes">
          <div className="flex flex-wrap gap-2">
            {CHANNELS.map((c) => {
              const on = channels.includes(c);
              return <button key={c} type="button" data-testid={`idea-drop-channel-${c}`} onClick={() => toggle(c)}
                className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${on ? "pf-gradient text-white border-transparent" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{on ? "✓ " : ""}{c}</button>;
            })}
          </div>
        </Field></div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Field label="Data"><Input type="date" value={dateStr} data-testid="idea-drop-date" onChange={(e) => setDateStr(e.target.value)} /></Field>
          <Field label="Horário"><Input type="time" value={time} data-testid="idea-drop-time" onChange={(e) => setTime(e.target.value)} /></Field>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Btn variant="ghost" data-testid="idea-drop-cancel" disabled={busy} onClick={onClose}>Cancelar</Btn>
          <Btn data-testid="idea-drop-confirm" disabled={busy} onClick={submit}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />} {busy ? "Gerando com IA..." : "Gerar posts"}
          </Btn>
        </div>
      </div>
    </div>
  );
}
